<template>
  <div
    class="artwork"
    :class="{ 'artwork--with-error': isGenericErrorVisible }"
    data-testid="transfers-artwork-info"
  >
    <div
      v-if="artwork"
      class="artwork__header"
      :class="{ 'artwork__header--with-error': isGenericErrorVisible }"
    >
      <div class="artwork__header-icon-wrapper">
        <UseSvg
          v-if="!!metadata || isGenericErrorVisible"
          :icon="headerIcon"
          class="artwork__header-icon"
        />
      </div>
      <span class="artwork__header-text">{{ artwork.name }}</span>
    </div>
    <div class="artwork__content">
      <ArtworkInfo
        :is-multi-upload="isMultiUpload"
        :artwork="artwork"
        class="artwork__sizes"
        :index="index"
      />
      <div>
        <ArtworkImage
          :index="index"
          :artwork="artwork"
          :is-multi-upload="isMultiUpload"
          :active-config-section="activeConfigSection"
          class="artwork__image"
          :show-full-screen-button="showFullScreenButton"
        />
        <div
          v-if="isStandardArtwork && !hasErrorMessage && hasArtwork"
          class="artwork__quality-config"
        >
          <DesignServicePromo
            v-if="displayEnhancementFeedback && paidExpertEditAddonEnabled && !isMultiUpload"
            @design-service-clicked="handleDesignServiceClick"
          />
          <ArtworkEnhancementFeedback
            v-if="displayEnhancementFeedback && !isMultiUpload"
            :artwork="artwork"
            :page-type="pdpType"
          />
          <ArtworkQualityConfigPanelAbAiMonetization
            v-if="enableAbAiMonetization"
            :index="index"
            :artwork="artwork"
            :reorder-enabled="reorderEnabled"
            :active-config-section="activeConfigSection"
            :paid-expert-edit-addon-enabled="paidExpertEditAddonEnabled"
            :is-multi-upload="isMultiUpload"
            @set-active-config-section="setActiveConfigSection"
          />
          <ArtworkQualityConfigPanel
            v-else
            :index="index"
            :artwork="artwork"
            :active-config-section="activeConfigSection"
            :is-multi-upload="isMultiUpload"
            @set-active-config-section="setActiveConfigSection"
          />
        </div>
      </div>
      <div
        class="artwork__info"
        :class="{ 'artwork__info--multi-size-presets': showMultiSize }"
        data-testid="transfers-artwork-info-section"
      >
        <StickersTabs
          v-if="isArtworkUploaded && isSticker && experiments.stickers_style_options"
          :tabs="tabs"
          :artwork="artwork"
          class="artwork__tabs"
        >
          <template #default="{ tab }">
            <!-- <StickersToolsShape
              v-if="tab === 'shape'"
              :artwork="artwork"
              class="artwork__section"
            /> -->
            <StickersToolsBackground
              v-if="tab === 'background'"
              :artwork="artwork"
              class="artwork__section"
            />
            <StickersToolsBorder
              v-else-if="tab === 'border'"
              :artwork="artwork"
              class="artwork__section"
            />
          </template>
        </StickersTabs>
        <ArtworkInfo
          :artwork="artwork"
          class="artwork__sizes artwork__sizes--desktop"
          :is-multi-upload="isMultiUpload"
          :index="index"
        />
        <ArtworkNotification v-if="!withError && warningMessage">
          <b>{{ warningMessage }}</b>
        </ArtworkNotification>
        <ArtworkError v-if="hasErrorMessage" :artwork="artwork" :current-store="currentStore" />
        <RedrawNotes
          v-if="showRedrawNote"
          ref="redrawNotesRef"
          :index="index"
          :enhancement-note="artwork.enhancementNote"
          :is-multi-upload="isMultiUpload"
        />
        <ReuploadBox v-if="showReuploadBox" :index="index" />
        <div v-else>
          <template v-if="showMultiSize">
            <h3 class="artwork__multi-size-title">Set Design Size</h3>
            <ArtworkMultiDimensions
              :artwork="artwork"
              :index="index"
              :disabled="artwork.isFile || disableCustomSizeInput"
              :readonly="printAsIs && !!metadata"
              :is-multi-upload="isMultiUpload"
              :show-free-transfers-badge="showFreeTransfersBadge"
            />
            <MultiSizeTotal :index="index" :is-multi-upload="isMultiUpload" />
          </template>
          <template v-else>
            <ArtworkDimensions
              :artwork="artwork"
              :index="index"
              :disabled="artwork.isFile || disableCustomSizeInput"
              :readonly="printAsIs && !!metadata"
              :current-store="currentStore"
              :is-multi-upload="isMultiUpload"
              @on-change="onDimensionsChange"
            />
            <ArtworkQuantity
              class="artwork__qty"
              :artwork="artwork"
              :index="index"
              :disabled="artwork.isFile"
              :readonly="printAsIs && !!metadata"
              :is-multi-upload="isMultiUpload"
              @change="onQtyChange"
            />
          </template>
          <TrimInfo
            v-if="isArtworkUploaded && !isStandardArtwork"
            :id="artwork.id"
            :artwork="artwork"
            :can-remove-bg="artwork.canRemoveBg"
            :remove-background="artwork.removeBackground"
            :toggle-background-error="artwork.toggleBackgroundError"
            reverse
            class="artwork__trim-info"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { useI18n } from "vue-i18n";
import StickersTabs from "components/jiffy/vue-product-details-dtf/Stickers/StickersTabs.vue";
import StickersToolsBorder from "components/jiffy/vue-product-details-dtf/Stickers/StickersTools/StickersToolsBorder.vue";
import StickersToolsBackground from "components/jiffy/vue-product-details-dtf/Stickers/StickersTools/StickersToolsBackground.vue";
// import StickersToolsShape from "components/jiffy/vue-product-details-dtf/Stickers/StickersTools/StickersToolsShape";
import ArtworkEnhancementFeedback from "components/jiffy/dtf-shared/ArtworkEnhancementFeedback.vue";
import UseSvg from "common/vue/components/UseSvg.vue";
import ArtworkImage from "./ArtworkImage.vue";
import DesignServicePromo from "./DesignServicePromo.vue";
import ArtworkQualityConfigPanel from "./ArtworkQualityConfigPanel.vue";
import ArtworkQualityConfigPanelAbAiMonetization from "./ArtworkQualityConfigPanel/ArtworkQualityConfigPanel.vue";
import ArtworkDimensions from "./ArtworkDimensions.vue";
import ArtworkMultiDimensions from "./ArtworkMultiSize/ArtworkMultiDimensions.vue";
import ArtworkQuantity from "./ArtworkQuantity.vue";
import ArtworkInfo from "./ArtworkInfo.vue";
import TrimInfo from "./TrimInfo.vue";
import ArtworkError from "./ArtworkError.vue";
import ArtworkNotification from "./ArtworkNotification.vue";
import ReuploadBox from "./ReuploadBox.vue";
import RedrawNotes from "./RedrawNotes.vue";
import MultiSizeTotal from "./ArtworkMultiSize/MultiSizeTotal.vue";
import { useDtfStore } from "./store";

const TABS = [
  { label: "Background", icon: "tools-background", value: "background" },
  { label: "Border", icon: "tools-border", value: "border" }
];

export default {
  name: "Artwork",
  components: {
    TrimInfo,
    ArtworkInfo,
    ArtworkImage,
    ArtworkEnhancementFeedback,
    DesignServicePromo,
    ArtworkDimensions,
    ArtworkQuantity,
    ArtworkError,
    UseSvg,
    ArtworkNotification,
    ReuploadBox,
    StickersTabs,
    StickersToolsBorder,
    StickersToolsBackground,
    RedrawNotes,
    ArtworkMultiDimensions,
    MultiSizeTotal,
    ArtworkQualityConfigPanel,
    ArtworkQualityConfigPanelAbAiMonetization
    // StickersToolsShape
  },
  props: {
    artwork: {
      type: Object,
      default: null
    },
    index: {
      type: Number,
      required: true
    },
    currentStore: {
      type: String,
      default: null
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    },
    showFullScreenButton: {
      type: Boolean,
      default: false
    },
    defaultActiveConfigSection: {
      type: String,
      default: "jiffy-print-perfect"
    },
    showFreeTransfersBadge: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return { ...useI18n() };
  },
  data() {
    return {
      tabs: TABS,
      width: null,
      height: null,
      quantity: null,
      sizeMessageMaxSize: 10,
      activeConfigSection: this.defaultActiveConfigSection
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "printAsIs",
      "pdpType",
      "sizeErrorTypes",
      "sticker",
      "minStickerQuantity",
      "isSpangle",
      "isSticker",
      "experiments",
      "showRedrawUI",
      "showMultiSize",
      "isStandardArtwork",
      "enableAbAiMonetization",
      "reorderEnabled",
      "paidExpertEditAddonEnabled",
      "lastConfirmedConfigSection"
    ]),
    metadata() {
      return this.artwork?.metadata;
    },
    withError() {
      return (
        this.metadata?.errors?.length > 0 ||
        this.artwork?.errorMessage ||
        this.artwork?.uploadError ||
        this.artwork?.retryFailed
      );
    },
    isMultiSizeError() {
      return this.artwork?.isMultiSizeError;
    },
    hasArtwork() {
      return this.artwork && !this.artwork.isSimulated && !this.artwork.uploadError;
    },
    hasErrorMessage() {
      const baseCondition = this.artwork && !this.artwork.isSimulated;

      return baseCondition && this.withError && !this.isMultiSizeError;
    },
    isMinQuantityInvalid() {
      return (
        this.sticker && this.artwork?.quantity && this.artwork?.quantity < this.minStickerQuantity
      );
    },
    isGenericErrorVisible() {
      return this.withError || this.isMinQuantityInvalid;
    },
    headerIcon() {
      return this.isGenericErrorVisible ? "strong-info" : "check-circle-bold";
    },
    uploadTimeMessage() {
      return this.isSpangle
        ? `Creating your spangle design! Please allow <b>up to ${this.t(
            "minute",
            this.remainingTime
          )}</b> to get your spangle design.`
        : `Proofing your image and making it print-ready. Please allow <b>up to ${this.t(
            "minute",
            this.remainingTime
          )}</b> to get your print-ready image.`;
    },
    warningMessage() {
      return this.artwork?.metadata?.warnings?.human;
    },
    reuploadTitle() {
      return this.printAsIs
        ? "Re-upload a gang sheet in the correct size"
        : "Re-upload valid artwork";
    },
    reuploadButton() {
      return this.printAsIs ? "Re-upload a gang sheet" : "Choose file";
    },
    showReuploadBox() {
      return !this.sizeErrorTypes.includes(this.artwork.errorType?.toString()) && this.withError;
    },
    fileSize() {
      return Math.floor((this.artwork?.file?.size || 0) * 0.000001 || 0);
    },
    remainingTime() {
      const spangleTime = this.isSpangle ? 1 : 0;

      if (Math.ceil(this.fileSize / 10) < 1) {
        return 1 + spangleTime;
      }
      return Math.min(Math.ceil(this.fileSize / 10), this.sizeMessageMaxSize) + spangleTime;
    },
    showUploadTimeMessage() {
      return this.artwork.isLoading;
    },
    isArtworkUploaded() {
      const artworkId = parseInt(this.artwork?.id, 10);
      if (Number.isNaN(artworkId)) {
        return false;
      }

      return typeof artworkId === "number" && !this.artwork?.isLoading;
    },
    disableCustomSizeInput() {
      return this.isStandardArtwork && !this.artwork?.enhancementCompleted;
    },
    showRedrawNote() {
      return !this.isStandardArtwork && this.showRedrawUI;
    },
    displayEnhancementFeedback() {
      return (
        this.enableAbAiMonetization &&
        this.artwork.id &&
        this.artwork.enhancementCompleted &&
        this.artwork.enhancementTier === "full_enhancements"
      );
    }
  },
  watch: {
    lastConfirmedConfigSection(val) {
      if (val && String(val.artworkId) === String(this.artwork?.id)) {
        this.activeConfigSection = val.section;
      }
    }
  },
  methods: {
    ...mapActions(useDtfStore, [
      "addFilesToQueue",
      "updateArtworkValuesById",
      "getArtworksPricing"
    ]),
    handleDesignServiceClick() {
      // Check the expert edit addon checkbox if not already checked
      if (!this.artwork.hasExpertEditAddon) {
        this.updateArtworkValuesById({
          id: this.artwork.id,
          hasExpertEditAddon: true,
          enhancementNote: ""
        });
        this.getArtworksPricing();
      }

      // Focus on the design instructions textarea
      this.$nextTick(() => {
        const $customNotesTextarea = this.$el.querySelector(".js-custom-notes-textarea");

        if ($customNotesTextarea) {
          $customNotesTextarea.focus();
          $customNotesTextarea.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (this.$refs.redrawNotesRef) {
          this.$refs.redrawNotesRef.focusTextarea();
        }
      });
    },
    onDimensionsChange({ height, width }) {
      this.height = height;
      this.width = width;
    },
    onQtyChange(quantity) {
      this.quantity = quantity;
    },
    trackAddFileAreaClick() {
      submitDtfEvent("replace_artwork_file_clicked", {
        page: "pdp",
        pdp_type: this.pdpType
      });
    },
    uploadClick() {
      this.trackAddFileAreaClick();
      this.$refs.file.click();
    },
    handleFileChange(event) {
      const { files } = event.target;

      if (!files.length) return;

      this.addFilesToQueue({ files, isDropEvent: false, artworkIndex: this.index });
      this.$refs.file.value = "";
    },
    setActiveConfigSection(section) {
      this.activeConfigSection = section;
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork {
  $this: &;

  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid $gray-border;

  &--with-error {
    border: 1px solid $red-border;
  }

  &__header {
    height: 36px;
    background: $gray-border;
    font-weight: 700;
    font-size: 20px;
    line-height: 100%;
    padding: 8px 25px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    border-radius: 8px 8px 0 0;
    display: flex;
    align-items: center;

    &--with-error {
      background: $red-error-background-2;

      #{$this}__header-icon {
        color: $red;
      }
    }
  }

  &__header-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    color: $feedback-success-darker;
  }

  &__header-text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__multi-size-title {
    color: $app-gray-modern-600;
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
    margin-top: 0;
    margin-bottom: 16px;
  }

  &__content {
    padding: 8px;
    display: flex;
    flex-direction: column;
    position: relative;

    @include with-tablet {
      padding: 16px;
    }

    @include with-large-desktop {
      padding: 18px 10px;
      flex-direction: row;

      :deep(.dtf-progress-bar__remove-button) {
        position: absolute;
        top: 4px;
      }
    }
  }

  &__file-info {
    margin-bottom: 32px;
  }

  &__file-info-row {
    font-size: 14px;
    font-weight: 400;
    line-height: 100%;

    & + & {
      margin-top: 8px;
    }
  }

  &__file-info-icon {
    width: 12px;
    height: 9px;
    margin: 0 8px 0 2px;
  }

  &__error-message {
    font-size: 14px;
    color: $red-light;
    margin-top: 5px;
  }

  &__qty {
    margin-top: 20px;
  }

  &__remove {
    right: 8px;
    top: 8px;
    position: absolute;
    font-size: 13px;
    font-weight: 700;
    line-height: 20px;
    color: $primary-light;
    background: $link-water;
    height: 32px;
    width: 90px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    border: 0;
    border-radius: 8px;

    &:focus,
    &:hover,
    &:active {
      background: $light-aqua-active;
    }
  }

  &__remove-icon {
    height: 18px;
    width: 18px;
  }

  &__trim-note {
    font-size: 12px;
    font-weight: 400;
    width: 100%;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    grid-gap: 4px;
    margin-top: 16px;
  }

  &__trim-icon {
    width: 18px;
    height: 18px;
  }

  &__quality-config {
    padding-top: 8px;
    margin-bottom: 28px;

    @media only screen and (min-width: $screen-xl-min) {
      margin-bottom: 0;
      padding-right: 16px;
      margin-right: 16px;
      border-right: 1px solid $gray-border;
      width: 400px;
    }
  }

  :deep(.artwork-image) {
    width: 100%;

    @include with-large-desktop {
      width: 400px;
      margin-right: 16px;
      padding-right: 16px;
      border-right: 1px solid $gray-border;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;
    max-width: 100%;
    width: 350px;

    &--multi-size-presets {
      width: 375px;
    }
  }

  &__trim-info {
    order: -1;
    margin-bottom: 8px;

    @include with-large-desktop {
      order: initial;
      margin-top: 16px;
    }
  }

  &__sizes {
    @include with-large-desktop {
      display: none;
    }

    &--desktop {
      display: none;

      @include with-large-desktop {
        display: flex;
      }
    }
  }

  &__section {
    margin-top: 16px;
  }

  &__tabs {
    margin-bottom: 24px;

    :deep(.stickers-tabs__items) {
      gap: 24px;
    }

    :deep(.stickers-border-size__item) {
      flex: 1;
      min-width: auto;
    }
  }
}
</style>
